import React, {FC, useState, useEffect} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { billingService } from '../../../../api';
import { getAuthenticatedApiUrl } from '../../../../api/authenticatedUrl';
import { BackArrow, Button, InfoBlock, Typography, Table } from '../../../../components';
import { InfoContainer, InfoTitle } from '../../../../components/info-block/styles';
import { useUi } from '../../../../context/ui.context';
import { icons } from '../../../../icons';
import { timeConverterMMDDYYHHMM } from '../../../../utils/dates';
import {
  Container,
  HeadWrapper,
  RightHeadContainer,
  InvoiceName,
  RowBodyWrapper,
  ColumnBodyWrapper,
  PaymentMethodContainer,
  ItemTitle,
  Divider,
  PriceTotalContainer,
  PriceTotalWrapper,
  PriceWrapper,
  InvoiceStatusContainer,
  InvoiceStatusText,
  LeftSide,
  TopInfoWrapper,
  PaymentSummary,
  PaymentSummaryLabel,
  PaymentAmount,
  PaymentMethodsList,
  PaymentMethodButton,
  PaymentMethodContent,
  PaymentProviderMark,
  PaymentMethodLabel,
  BalancePayBadge,
  PaymentButtonSpinner,
  NoPaymentMethods,
  InvoiceItemContainer,
} from './styles';
import type { PaymentProvider } from './styles';
import ResourceItem from './resourceItem';
import { formatCurrency } from '../../../../utils/display-price';
import InvoiceSkeleton from './skeleton'
import { handleStatusName } from '../../../../utils/handle-status-name';
import { permissionSurfaces, usePermission, usePermissionDecision } from '../../../../permissions';

type PaymentMethod = {
  id?: string | number,
  value: string,
  label: string,
  name?: string,
  display_name?: string,
  gateway_name?: string,
  pay_method_id?: string | number,
}

const getPaymentProvider = (paymentMethod: PaymentMethod): PaymentProvider => {
  if (paymentMethod.value === 'credit_balance') return 'credit'

  const providerName = [
    paymentMethod.name,
    paymentMethod.gateway_name,
    paymentMethod.display_name,
    paymentMethod.label,
  ].filter(Boolean).join(' ').toLowerCase()

  if (providerName.includes('paypal')) return 'paypal'
  if (providerName.includes('stripe')) return 'stripe'
  if (providerName.includes('liqpay')) return 'liqpay'
  if (providerName.includes('fondy')) return 'fondy'
  if (providerName.includes('monobank') || providerName.includes('mono bank')) return 'monobank'
  if (providerName.includes('portmone')) return 'portmone'
  if (providerName.includes('nowpayments') || providerName.includes('crypto') || providerName.includes('coin') || providerName.includes('bitcoin')) return 'crypto'
  if (providerName.includes('wayforpay') || providerName.includes('visa') || providerName.includes('mastercard') || providerName.includes('card')) return 'wayforpay'

  return 'default'
}

const PaymentProviderIcon: FC<{provider: PaymentProvider, label: string}> = ({provider, label}) => {
  if (provider === 'wayforpay') {
    return (
      <>
        <svg className="visa-logo" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M9.112 8.262 5.97 15.758H3.92L2.374 9.775c-.094-.368-.175-.503-.461-.658C1.447 8.864.677 8.627 0 8.479l.046-.217h3.3a.904.904 0 0 1 .894.764l.817 4.338 2.018-5.102zm8.033 5.049c.008-1.979-2.736-2.088-2.717-2.972.006-.269.262-.555.822-.628a3.66 3.66 0 0 1 1.913.336l.34-1.59a5.207 5.207 0 0 0-1.814-.333c-1.917 0-3.266 1.02-3.278 2.479-.012 1.079.963 1.68 1.698 2.04.756.367 1.01.603 1.006.931-.005.504-.602.725-1.16.734-.975.015-1.54-.263-1.992-.473l-.351 1.642c.453.208 1.289.39 2.156.398 2.037 0 3.37-1.006 3.377-2.564m5.061 2.447H24l-1.565-7.496h-1.656a.883.883 0 0 0-.826.55l-2.909 6.946h2.036l.405-1.12h2.488zm-2.163-2.656 1.02-2.815.588 2.815zm-8.16-4.84-1.603 7.496H8.34l1.605-7.496z" />
        </svg>
        <svg className="mastercard-logo" viewBox="0 0 38 24" aria-hidden="true">
          <circle cx="14" cy="12" r="10" fill="#EB001B" />
          <circle cx="24" cy="12" r="10" fill="#F79E1B" fillOpacity=".9" />
        </svg>
      </>
    )
  }

  if (provider === 'paypal') {
    return (
      <svg className="paypal-logo" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#27346A" d="M15.607 4.653H8.941L6.645 19.251H1.82L4.862 0h7.995c3.754 0 6.375 2.294 6.473 5.513-.648-.478-2.105-.86-3.722-.86m6.57 5.546c0 3.41-3.01 6.853-6.958 6.853h-2.493L11.595 24H6.74l1.845-11.538h3.592c4.208 0 7.346-3.634 7.153-6.949a5.24 5.24 0 0 1 2.848 4.686M9.653 5.546h6.408c.907 0 1.942.222 2.363.541-.195 2.741-2.655 5.483-6.441 5.483H8.714Z" />
      </svg>
    )
  }

  if (provider === 'stripe') {
    return (
      <svg className="stripe-logo" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.594-7.305h.003z" />
      </svg>
    )
  }

  if (provider === 'crypto') {
    return (
      <svg className="bitcoin-logo" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M23.638 14.904c-1.602 6.43-8.113 10.34-14.542 8.736C2.67 22.05-1.244 15.525.362 9.105 1.962 2.67 8.475-1.243 14.9.358c6.43 1.605 10.342 8.115 8.738 14.548v-.002zm-6.35-4.613c.24-1.59-.974-2.45-2.64-3.03l.54-2.153-1.315-.33-.525 2.107c-.345-.087-.705-.167-1.064-.25l.526-2.127-1.32-.33-.54 2.165c-.285-.067-.565-.132-.84-.2l-1.815-.45-.35 1.407s.975.225.955.236c.535.136.63.486.615.766l-1.477 5.92c-.075.166-.24.406-.614.314.015.02-.96-.24-.96-.24l-.66 1.51 1.71.426.93.242-.54 2.19 1.32.327.54-2.17c.36.1.705.19 1.05.273l-.51 2.154 1.32.33.545-2.19c2.24.427 3.93.257 4.64-1.774.57-1.637-.03-2.58-1.217-3.196.854-.193 1.5-.76 1.68-1.93h.01zm-3.01 4.22c-.404 1.64-3.157.75-4.05.53l.72-2.9c.896.23 3.757.67 3.33 2.37zm.41-4.24c-.37 1.49-2.662.735-3.405.55l.654-2.64c.744.18 3.137.524 2.75 2.084v.006z" />
      </svg>
    )
  }

  const providerWordmarks: Partial<Record<PaymentProvider, string>> = {
    liqpay: 'LiqPay',
    fondy: 'FONDY',
    monobank: 'mono',
    portmone: 'PORTMONE',
  }

  return <>{providerWordmarks[provider] || label.trim().charAt(0).toUpperCase()}</>
}

const InvoicPage:FC = () => {
  const [invoice, setInvoice] = useState<any>({})
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [payingMethod, setPayingMethod] = useState<string | null>(null)
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [journal, setJournal] = useState<any[]>([])
  const [items, setItems] = useState<any[]>([])
  const {invoiceId} = useParams()
  const {viewAlert} = useUi()
  const [t] = useTranslation()
  const navigate = useNavigate()
  const { mergeEntityPermissions } = usePermission()
  const canDownloadInvoice = usePermissionDecision(permissionSurfaces.detailActions.billingInvoiceDownload).allowed
  const canPayInvoice = usePermissionDecision(permissionSurfaces.detailActions.billingInvoicePay).allowed
  const getInvoice = () => {
    if(invoiceId){
      
      return billingService.getInvoice(invoiceId)
      .then((res:any) => {
        mergeEntityPermissions('billing', res.data, `Merged billing invoice ${invoiceId} detail permissions`)
        setInvoice(res.data)
        if(res.data?.payment_options?.gateways){
          const isCreditTopUpInvoice = res.data.items?.some((item:any) => item.item_type === 'credit')
          const creditPaymentMethod: PaymentMethod[] = res.data.payment_options.canPayWithCredit && !isCreditTopUpInvoice
            ? [{id:'credit_balance', value:'credit_balance', label:t("CREDIT_BALANCE")}]
            : []

          setPaymentMethods([...creditPaymentMethod, ...res.data.payment_options.gateways.map((item:any) => {
            return {
              ...item,
              id:`${item.id}`,
              value:`${item.id}`,
              label:item.display_name || item.name || t("PAYMENT_METHOD")
            }
          })])
        }
        if(res.data.items?.length > 0){
          setItems(res.data.items.map((item:any) => {
            return {
              ...item
            }
          }))
        }
        if(res.data.journal.length > 0){
          setJournal(res.data.journal.map((item:any) => {
            return {
              ...item,
              date_added:timeConverterMMDDYYHHMM(item.date_added),
              gateway_display_name:item.transaction ? item.transaction.gateway_display_name : item.destination_name,
              amount:`${item.source_amount}`
            }
          }))
        }
      })
      .catch((err:any) => {
        if(err?.response?.data?.detail){
          viewAlert({
            severity: 'error',
            message:err.response?.data?.detail
          })
        }
        navigate(-1)
      })
      .finally(() => {
        setPageLoading(false)
      })
    }
  }
  
  useEffect(() => {
    getInvoice()
  },[])
  const downloadPdf = async () => {
    try {
      window.location.href = await getAuthenticatedApiUrl(`billing/invoices/${invoice.id}/download`, {content_type: 'pdf'})
    } catch {
      viewAlert({severity: 'error', message: `${t('ERROR_OCCURRED')}`})
    }
  }
  const handlePayment = async (paymentMethod: PaymentMethod) => {
    setPayingMethod(paymentMethod.value)
    let paymentWindow: Window | null = null
    try{
      if(paymentMethod.value === 'credit_balance'){
        const paymantRes = await billingService.payFromCredit(invoice.id)
        if(paymantRes?.data?.detail){
          await viewAlert({
            severity:'info',
            message:paymantRes?.data?.detail
          })
        }
        await getInvoice()
        return
      }

      const gatewayName = paymentMethod.gateway_name || paymentMethod.name
      if (!gatewayName) throw new Error(t("ERROR_OCCURRED"))

      const params: Record<string, string> = {
        invoice: String(invoice.id),
      }
      if (paymentMethod.pay_method_id) {
        params.pay_method_id = String(paymentMethod.pay_method_id)
      }

      // Open during the click event, before awaiting the token refresh.
      paymentWindow = window.open('about:blank', '_blank')
      if (paymentWindow) paymentWindow.opener = null
      const url = await getAuthenticatedApiUrl(`billing/gateway/${encodeURIComponent(gatewayName)}/pay_invoice`, params)
      if (paymentWindow) {
        paymentWindow.location.replace(url)
      } else {
        window.location.href = url
      }
    } catch (err:any) {
      paymentWindow?.close()
      if(err?.response?.data?.detail){
        viewAlert({
          severity:'error',
          message:err.response?.data?.detail
        })
      } else {
        viewAlert({
          severity:'error',
          message:err?.message || `${t("ERROR_OCCURRED")}`
        })
      }
    } finally {
      setPayingMethod(null)
    }
  }
  const ColumnsJournal = [
    {
      title:t("DATE_ADDED"),
      key:'date_added'
    },
    {
      title:t("PAYMENT_METHOD"),
      key:'gateway_display_name'
    },
    {
      title:t("DESTINATION_NAME"),
      key:'destination_name'
    },
    {
      title:t("AMOUNT"),
      key:'amount'
    },
  ]
  
  if(pageLoading) return <InvoiceSkeleton pageLoading={pageLoading}/>
  return (
    <Container>
      <BackArrow onClick={() => navigate('/billing/invoices')} title={t("BACK_TO_BILLING")}/>
      <HeadWrapper>
        <LeftSide>
          <InvoiceName>
            {invoice.name || invoice.id}
          </InvoiceName>
          <InvoiceStatusContainer status={invoice.status}>
            <InvoiceStatusText status={invoice.status}>
              {handleStatusName(invoice.status, t)}
            </InvoiceStatusText>
          </InvoiceStatusContainer>
        </LeftSide>
        <RightHeadContainer>
          {canDownloadInvoice ? (
            <Button
              variant="stroke"
              size='small'
              icon={icons.Download}
              title={t("DOWNLOAD_PDF")}
              onClick={downloadPdf}
            />
          ) : null}
        </RightHeadContainer>
      </HeadWrapper>
      <RowBodyWrapper>
        <ColumnBodyWrapper>
          <TopInfoWrapper>
            <InfoBlock
              customStyles={{ marginBottom:'0px'}}
              title={t("TIMELINE")}
              data={[
                {
                  title:t("ISSUE_DATE"),
                  id:'1',
                  value:timeConverterMMDDYYHHMM(invoice.issue_date),
                },
                {
                  title:t("DUE_DATE"),
                  id:'2',
                  value:timeConverterMMDDYYHHMM(invoice.due_date),
                },
              ]}
            />
            <InfoBlock
              customStyles={{ marginBottom:'0px'}}
              title={t("TO")}
              noTitles
              data={[
                {
                  title:'',
                  id:'1',
                  value:`${invoice.first_name} ${invoice.last_name}`,
                },
                // {
                //   title:'',
                //   id:'2',
                //   value:invoice.address1,
                // },
                // {
                //   title:'',
                //   id:'3',
                //   value:invoice.city,
                // },
                {
                  title:'',
                  id:'4',
                  value:invoice.phone,
                },
              ]}
            />
          </TopInfoWrapper>
            <InfoContainer style={{display:'flex', flex:'auto 1', marginBottom:'0px'}}>
              <InfoTitle style={{marginBottom:'10px'}}>
                {t("ITEMS_BILLING")}
              </InfoTitle>
              <InfoContainer style={{display:'flex', flex:'auto 1', marginBottom:'0px'}}>
                {items.map((item:any) => (
                  <InvoiceItemContainer key={item.id}>
                    <ItemTitle>{item.description}</ItemTitle>
                    {item.usage?.resource_types?.length > 0 &&
                      <Divider/>
                    }
                    {item.usage?.resource_types.map((resourceType:any) => (
                      <ResourceItem key={resourceType.name} resourceType={resourceType} items={item.usage.resource_usages.filter((re:any) => re.resource_type.name === resourceType.name)}/>
                    ))}
                  </InvoiceItemContainer>
                ))}
                <PriceTotalWrapper>
                  <PriceTotalContainer>
                    <PriceWrapper>
                      <Typography variant='body'>
                        {t("SUBTOTAL")}
                      </Typography>
                      <Typography variant='body-medium'>
                        {formatCurrency(invoice.subtotal, invoice.currency)}
                      </Typography>
                    </PriceWrapper>
                  </PriceTotalContainer>
                  <PriceTotalContainer>
                    <PriceWrapper>
                      <Typography variant='body'>
                        {t("TOTAL")}
                      </Typography>
                      <Typography variant='body-medium'>
                        {formatCurrency(invoice.total, invoice.currency)}
                      </Typography>
                    </PriceWrapper>
                  </PriceTotalContainer>
                </PriceTotalWrapper>
              </InfoContainer>
            </InfoContainer>
            <InfoContainer style={{display:'flex', flex:'auto 1', marginBottom:'0px'}}>
              <InfoTitle style={{marginBottom:'10px'}}>
                {t("JOURNAL")}
              </InfoTitle>
              {invoice.journal.length === 0 ?
                <Typography variant='body'>{t("NO_ENTRIES_FOUND")}</Typography>
              :
                <Table
                  columns={ColumnsJournal}
                  rows={journal}
                  hideOther
                  hideCheckbox
                  // paginationData={paginationData}
                />
              }
            </InfoContainer>
        </ColumnBodyWrapper>
        {invoice.status !== 'paid' && canPayInvoice ?
          <PaymentMethodContainer>
            <PaymentSummary>
              <PaymentSummaryLabel>{t("PAY_WITH")}</PaymentSummaryLabel>
              <PaymentAmount>{formatCurrency(invoice.balance, invoice.currency)}</PaymentAmount>
            </PaymentSummary>
            {paymentMethods.length > 0 ?
              <PaymentMethodsList>
                {paymentMethods.map((paymentMethod) => {
                  const provider = getPaymentProvider(paymentMethod)
                  const isLoading = payingMethod === paymentMethod.value
                  const paymentMethodLabel = provider === 'wayforpay' && !paymentMethod.pay_method_id
                    ? 'Visa / Mastercard'
                    : provider === 'crypto' && paymentMethod.name?.toLowerCase() === 'nowpayments'
                      ? 'NOWPayments'
                    : paymentMethod.label

                  return (
                    <PaymentMethodButton
                      key={paymentMethod.value}
                      type="button"
                      $provider={provider}
                      disabled={Boolean(payingMethod)}
                      aria-busy={isLoading}
                      aria-label={`${t("PAY_NOW")}: ${paymentMethod.label}`}
                      onClick={() => handlePayment(paymentMethod)}
                    >
                      <PaymentMethodContent>
                        {provider === 'credit' ?
                          <>
                            <PaymentMethodLabel>{paymentMethodLabel}</PaymentMethodLabel>
                            <BalancePayBadge>{t("PAY")}</BalancePayBadge>
                          </>
                        : provider === 'wayforpay' ?
                          <>
                            <PaymentMethodLabel>{paymentMethodLabel}</PaymentMethodLabel>
                            <PaymentProviderMark $provider={provider}>
                              <PaymentProviderIcon provider={provider} label={paymentMethodLabel}/>
                            </PaymentProviderMark>
                          </>
                        :
                          <>
                            <PaymentProviderMark $provider={provider}>
                              <PaymentProviderIcon provider={provider} label={paymentMethodLabel}/>
                            </PaymentProviderMark>
                            <PaymentMethodLabel>{paymentMethodLabel}</PaymentMethodLabel>
                          </>
                        }
                      </PaymentMethodContent>
                      {isLoading ? <PaymentButtonSpinner aria-hidden="true"/> : null}
                    </PaymentMethodButton>
                  )
                })}
              </PaymentMethodsList>
            :
              <NoPaymentMethods>{t("NO_PAYMENT_METHODS")}</NoPaymentMethods>
            }
          </PaymentMethodContainer>
        : null}
      </RowBodyWrapper>
    </Container>
  )
}

export default InvoicPage
